import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Calendar, Search, Plus, Loader2, LogOut, Phone, Cake, Edit, Trash2, ChevronRight, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Simple session token reader (matches existing AdminDashboard.js convention)
function getAuthHeaders() {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function AcharyaDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/auth/me`, { headers: getAuthHeaders() });
      if (res.data?.role !== 'admin') {
        navigate('/admin/login');
        return false;
      }
      return true;
    } catch {
      navigate('/admin/login');
      return false;
    }
  }, [navigate]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        axios.get(`${API}/api/acharya/dashboard/stats`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/acharya/clients`, { params: { q: search || undefined, limit: 100 }, headers: getAuthHeaders() }),
      ]);
      setStats(s.data);
      setClients(c.data.items || []);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) navigate('/admin/login');
    } finally {
      setLoading(false);
    }
  }, [search, navigate]);

  useEffect(() => {
    checkAuth().then((ok) => { if (ok) loadAll(); });
  }, [checkAuth, loadAll]);

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    navigate('/admin/login');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('क्या आप वाकई इस क्लाइंट को हटाना चाहते हैं?')) return;
    try {
      await axios.delete(`${API}/api/acharya/clients/${id}`, { headers: getAuthHeaders() });
      loadAll();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  return (
    <div className="min-h-screen bg-bg-primary text-text-main">
      {/* Header */}
      <div className="border-b border-gold/20 bg-gradient-to-r from-purple-950/40 via-bg-primary to-purple-950/40 backdrop-blur-md sticky top-0 z-30">
        <div className="container-custom flex items-center justify-between py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center text-gold text-xl">🕉️</div>
            <div>
              <h1 className="font-heading text-xl text-gold">आचार्य Dashboard</h1>
              <p className="text-xs text-text-muted">Pro Astrologer — जन्मपत्री CRM</p>
            </div>
          </div>
          <button onClick={handleLogout} data-testid="acharya-logout-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 text-sm">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </div>

      <div className="container-custom py-8 space-y-8">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard icon={Users} label="कुल क्लाइंट" value={stats?.total_clients || 0} color="from-purple-600/30 to-purple-900/20" testid="stat-total-clients" />
          <StatCard icon={Calendar} label="आज की Consultations" value={stats?.todays_consultations || 0} color="from-amber-600/25 to-amber-900/15" testid="stat-todays-consultations" />
          <StatCard icon={Cake} label="इस हफ्ते के Birthdays" value={stats?.upcoming_birthdays?.length || 0} color="from-rose-600/25 to-rose-900/15" testid="stat-upcoming-birthdays" />
        </div>

        {/* Upcoming birthdays panel */}
        {stats?.upcoming_birthdays?.length > 0 && (
          <div className="bg-rose-500/5 border border-rose-400/30 rounded-2xl p-5" data-testid="birthdays-panel">
            <div className="flex items-center gap-2 text-rose-300 font-semibold mb-3">
              <Cake className="w-4 h-4" /> इस हफ्ते जन्मदिन
            </div>
            <div className="space-y-2">
              {stats.upcoming_birthdays.map((b) => (
                <div key={b.id} className="flex items-center justify-between text-sm">
                  <span className="text-text-main">{b.name} <span className="text-text-muted">— {b.date}</span></span>
                  <span className="px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-200 text-xs">
                    {b.in_days === 0 ? 'आज!' : `${b.in_days} दिन में`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              type="text"
              placeholder="नाम / फोन / ईमेल खोजें..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="client-search-input"
              className="w-full pl-10 pr-4 py-2.5 rounded-full bg-white/5 border border-white/10 focus:border-gold/50 outline-none text-sm"
            />
          </div>
          <button
            onClick={() => { setEditing(null); setShowAddForm(true); }}
            data-testid="add-client-btn"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-gold text-bg-primary font-semibold hover:bg-yellow-400 transition"
          >
            <Plus className="w-4 h-4" /> नया Client जोड़ें
          </button>
        </div>

        {/* Client list */}
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>
        ) : clients.length === 0 ? (
          <EmptyState onAdd={() => { setEditing(null); setShowAddForm(true); }} />
        ) : (
          <div className="grid gap-3" data-testid="clients-list">
            {clients.map((c) => (
              <ClientRow key={c.id} client={c} onEdit={() => { setEditing(c); setShowAddForm(true); }} onDelete={() => handleDelete(c.id)} onOpen={() => navigate(`/acharya-dashboard/client/${c.id}`)} />
            ))}
          </div>
        )}
      </div>

      {showAddForm && (
        <ClientFormModal
          editing={editing}
          onClose={() => { setShowAddForm(false); setEditing(null); }}
          onSaved={(newId) => {
            setShowAddForm(false);
            setEditing(null);
            loadAll();
            // Auto-navigate to the new client's Kundli page (Phase B1+ workstation)
            if (newId) {
              setTimeout(() => navigate(`/acharya-dashboard/client/${newId}`), 250);
            }
          }}
        />
      )}
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, color, testid }) => (
  <div className={`rounded-2xl p-5 border border-white/10 bg-gradient-to-br ${color}`} data-testid={testid}>
    <Icon className="w-5 h-5 text-gold mb-2" />
    <div className="text-3xl font-bold text-text-main">{value}</div>
    <div className="text-xs uppercase tracking-widest text-text-muted mt-1">{label}</div>
  </div>
);

const ClientRow = ({ client, onEdit, onDelete, onOpen }) => (
  <div className="flex items-center gap-4 px-4 py-3 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition" data-testid={`client-row-${client.id}`}>
    <button onClick={onOpen} className="flex-1 text-left">
      <div className="font-medium text-text-main">{client.name}</div>
      <div className="text-xs text-text-muted mt-0.5">
        🎂 {client.dob} {client.tob} · 📍 {client.pob} {client.phone ? `· 📞 ${client.phone}` : ''}
      </div>
    </button>
    <button onClick={onEdit} data-testid={`edit-client-${client.id}`} className="p-2 rounded-full hover:bg-white/10" title="Edit">
      <Edit className="w-4 h-4 text-text-muted" />
    </button>
    <button onClick={onDelete} data-testid={`delete-client-${client.id}`} className="p-2 rounded-full hover:bg-red-500/15" title="Delete">
      <Trash2 className="w-4 h-4 text-red-400" />
    </button>
    <button onClick={onOpen} className="p-2 rounded-full bg-gold/10 hover:bg-gold/20" title="कुंडली विश्लेषण" data-testid={`open-client-${client.id}`}>
      <ChevronRight className="w-4 h-4 text-gold" />
    </button>
  </div>
);

const EmptyState = ({ onAdd }) => (
  <div className="text-center py-16 border border-dashed border-white/15 rounded-2xl">
    <Sparkles className="w-10 h-10 text-gold/60 mx-auto mb-3" />
    <h3 className="font-heading text-xl text-text-main mb-2">अभी कोई client नहीं है</h3>
    <p className="text-sm text-text-muted mb-5">पहला client जोड़कर शुरुआत करें — कुंडली, दशा, गोचर सब एक जगह।</p>
    <button onClick={onAdd} data-testid="empty-add-client-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gold text-bg-primary font-semibold">
      <Plus className="w-4 h-4" /> पहला Client जोड़ें
    </button>
  </div>
);

const ClientFormModal = ({ editing, onClose, onSaved }) => {
  const [form, setForm] = useState(() => ({
    name: editing?.name || '',
    dob: editing?.dob || '',
    tob: editing?.tob || '',
    pob: editing?.pob || '',
    gender: editing?.gender || '',
    phone: editing?.phone || '',
    email: editing?.email || '',
    gotra: editing?.gotra || '',
    notes: editing?.notes || '',
  }));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.dob || !form.tob || !form.pob) {
      setError('नाम, जन्म तिथि, समय, स्थान — चारों भरना ज़रूरी है।');
      return;
    }
    setSubmitting(true);
    setError('');
    // Strip empty-string optional fields so backend Optional[EmailStr]/Optional[str]
    // validators don't reject "" as invalid (treat empty UI input as "not provided").
    const payload = Object.fromEntries(
      Object.entries(form).filter(([_, v]) => v !== '' && v != null)
    );
    try {
      if (editing) {
        await axios.patch(`${API}/api/acharya/clients/${editing.id}`, payload, { headers: getAuthHeaders() });
        toast.success('Client update हो गया ✅');
        onSaved();
      } else {
        const res = await axios.post(`${API}/api/acharya/clients`, payload, { headers: getAuthHeaders() });
        const newId = res?.data?.client?.id;
        toast.success(`Client जोड़ दिया गया ✅ ${form.name}`);
        onSaved(newId);   // pass new id so parent can navigate
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      let msg = 'सेव नहीं हो पाया। दोबारा कोशिश करें।';
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length) {
        // Pydantic validation errors are an array of {loc, msg, type}
        const first = detail[0];
        const field = Array.isArray(first.loc) ? first.loc.slice(-1)[0] : 'फ़ील्ड';
        msg = `${field}: ${first.msg || 'अमान्य मान'}`;
      }
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-bg-primary border border-gold/30 rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="client-form-modal">
        <h2 className="font-heading text-xl text-gold mb-1">{editing ? 'Client Edit करें' : 'नया Client जोड़ें'}</h2>
        <p className="text-xs text-text-muted mb-5">PII (DOB, फोन) automatically encrypted होगा।</p>
        <form onSubmit={submit} className="space-y-3">
          <Field label="नाम *" value={form.name} onChange={handle('name')} testid="form-name" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="जन्म तिथि *" value={form.dob} onChange={handle('dob')} placeholder="YYYY-MM-DD" type="date" testid="form-dob" />
            <Field label="जन्म समय *" value={form.tob} onChange={handle('tob')} placeholder="HH:MM" type="time" testid="form-tob" />
          </div>
          <Field label="जन्म स्थान *" value={form.pob} onChange={handle('pob')} placeholder="Faridabad" testid="form-pob" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="लिंग" value={form.gender} onChange={handle('gender')} placeholder="M / F" testid="form-gender" />
            <Field label="गोत्र" value={form.gotra} onChange={handle('gotra')} placeholder="Bhardwaj" testid="form-gotra" />
          </div>
          <Field label="WhatsApp" value={form.phone} onChange={handle('phone')} placeholder="+91 98XXX..." testid="form-phone" />
          <Field label="Email" value={form.email} onChange={handle('email')} type="email" testid="form-email" />
          <div>
            <label className="text-xs uppercase tracking-wider text-text-muted block mb-1">Notes</label>
            <textarea value={form.notes} onChange={handle('notes')} rows="3" data-testid="form-notes" className="w-full bg-white/10 border border-white/15 rounded-xl px-3 py-2 text-sm focus:border-gold/50 outline-none" />
          </div>
          {error && <p className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{error}</p>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 text-sm">रद्द करें</button>
            <button type="submit" disabled={submitting} data-testid="form-submit" className="flex-1 px-4 py-2.5 rounded-full bg-gold text-bg-primary font-semibold disabled:opacity-60">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin inline" /> : (editing ? 'Update करें' : 'सेव करें')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, placeholder, type = 'text', testid }) => (
  <div>
    <label className="text-xs uppercase tracking-wider text-text-muted block mb-1">{label}</label>
    <input type={type} value={value} onChange={onChange} placeholder={placeholder} data-testid={testid} className="w-full bg-white/10 border border-white/15 rounded-xl px-3 py-2 text-sm focus:border-gold/50 outline-none" />
  </div>
);
